import { conformsTo, isArrayAndEveryElement, isBoolean, isNullable, isNumber, isString } from '@dmb/core';

/**
 * Response of `GET /api/v1/sys/monitor/trend-imports?start=YYYY-MM-DD`.
 * Mirrors the data of the legacy PHP `TrendReportImportMonitorPage`.
 */
export type TrendImportItemStatus = 'finished' | 'running' | 'scheduled' | 'missing' | 'failed';

/** One expected/delivered file of a sales import profile for a day */
export type TrendImportItem = {
  /** `null` when no import exists yet (e.g. file not delivered) */
  import_id?: number | null;
  profile_id: number;
  account?: string | null;
  file_name?: string | null;
  status: TrendImportItemStatus;
  /** Current stage: extract | transform | load */
  stage?: string | null;
  /** 0..1, `-1` = failed */
  progress: number;
  total_rows?: number | null;
  written_rows?: number | null;
  comment?: string | null;
};

export type TrendImportDayStatus = {
  /** Day in `YYYY-MM-DD` */
  date: string;
  /** 0..1, `-1` = unknown/unsure status (rendered as error) */
  progress: number;
  comment?: string | null;
  imports: TrendImportItem[];
};

export type TrendImportStoreStatus = {
  store_id: number;
  store_name: string;
  imported_by_kotlin: boolean;
  /** Newest day first */
  days: TrendImportDayStatus[];
};

export type TrendImportMonitorModel = {
  /** ISO timestamp of the status calculation */
  generated_at: string;
  stores: TrendImportStoreStatus[];
};

const isTrendImportItemStatus = (value: unknown): value is TrendImportItemStatus =>
  value === 'finished' || value === 'running' || value === 'scheduled' || value === 'missing' || value === 'failed';

const isTrendImportItem = conformsTo<TrendImportItem>({
  import_id: isNullable(isNumber),
  profile_id: isNumber,
  account: isNullable(isString),
  file_name: isNullable(isString),
  status: isTrendImportItemStatus,
  stage: isNullable(isString),
  progress: isNumber,
  total_rows: isNullable(isNumber),
  written_rows: isNullable(isNumber),
  comment: isNullable(isString),
});

const isTrendImportDayStatus = conformsTo<TrendImportDayStatus>({
  date: isString,
  progress: isNumber,
  comment: isNullable(isString),
  imports: isArrayAndEveryElement(isTrendImportItem),
});

const isTrendImportStoreStatus = conformsTo<TrendImportStoreStatus>({
  store_id: isNumber,
  store_name: isString,
  imported_by_kotlin: isBoolean,
  days: isArrayAndEveryElement(isTrendImportDayStatus),
});

export const isTrendImportMonitorModel = conformsTo<TrendImportMonitorModel>({
  generated_at: isString,
  stores: isArrayAndEveryElement(isTrendImportStoreStatus),
});
